import React, { useCallback, useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Activity } from "lucide-react";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="grid-bg" style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
      <div style={{ width: "100%", maxWidth: 460 }}>
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none", marginBottom: 28 }}>
          <div style={{ width: 32, height: 32, background: "var(--amber-500)", display: "grid", placeItems: "center", borderRadius: 2 }}>
            <Activity size={18} color="var(--navy-900)" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-serif-display" style={{ fontSize: 16, fontWeight: 700, color: "var(--ink)", lineHeight: 1 }}>CanadaMRO</div>
            <div className="font-mono" style={{ fontSize: 10, color: "var(--ink-mute)", letterSpacing: ".15em", marginTop: 2 }}>EXCHANGE</div>
          </div>
        </Link>
        <div className="card" style={{ padding: 32 }}>
          <h1 className="font-serif-display" style={{ fontSize: 28, fontWeight: 600, margin: 0 }}>{title}</h1>
          {subtitle && <p style={{ color: "var(--ink-dim)", fontSize: 14, marginTop: 8 }}>{subtitle}</p>}
          <div style={{ marginTop: 24 }}>{children}</div>
        </div>
      </div>
    </div>
  );
}

export function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { await login(email, pw); nav("/app/dashboard"); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <AuthShell title="Log in to your account" subtitle="Buyers, suppliers and admins.">
      <form onSubmit={submit} style={{ display: "grid", gap: 16 }}>
        <div>
          <label className="label">Email</label>
          <input className="input" data-testid="login-email" value={email} onChange={e => setEmail(e.target.value)} required type="email" placeholder="you@company.ca" />
        </div>
        <div>
          <label className="label">Password</label>
          <input className="input" data-testid="login-password" value={pw} onChange={e => setPw(e.target.value)} required type="password" placeholder="••••••••" />
        </div>
        {err && <div style={{ color: "var(--red)", fontSize: 13 }} data-testid="login-error">{err}</div>}
        <button className="btn btn-primary" data-testid="login-submit" disabled={busy} style={{ justifyContent: "center", padding: 12 }}>
          {busy ? "Signing in…" : "Log in"}
        </button>
        <div style={{ fontSize: 13, color: "var(--ink-dim)", textAlign: "center" }}>
          No account? <Link to="/register" style={{ color: "var(--amber-400)" }}>Request access</Link>
          <span style={{ margin: "0 8px", color: "var(--ink-mute)" }}>·</span>
          <Link to="/forgot-password" style={{ color: "var(--amber-400)" }}>Forgot password?</Link>
        </div>
        <div className="font-mono" style={{ fontSize: 11, color: "var(--ink-mute)", background: "var(--navy-900)", padding: 12, borderRadius: 4, border: "1px dashed var(--line)", lineHeight: 1.6 }}>
          DEMO · admin@canadamro.ca / Admin@123<br/>
          DEMO · buyer1@acmeindustrial.ca / Buyer@123<br/>
          DEMO · supplier1@northpartsco.ca / Supplier@123
        </div>
      </form>
    </AuthShell>
  );
}

export function RegisterPage() {
  // Wrap only this route with the provider so the script + badge stay scoped to /register.
  if (RECAPTCHA_SITE_KEY) {
    return (
      <GoogleReCaptchaProvider
        reCaptchaKey={RECAPTCHA_SITE_KEY}
        scriptProps={{ async: true, defer: true, appendTo: "head" }}
        container={{ parameters: { badge: "bottomright" } }}
      >
        <RegisterForm />
      </GoogleReCaptchaProvider>
    );
  }
  // No key configured (dev / preview without env) — render the form without reCAPTCHA.
  return <RegisterForm />;
}

function RegisterForm() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    email: "", password: "", full_name: "", company_name: "",
    role: params.get("role") === "supplier" ? "supplier" : "buyer", phone: ""
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const { executeRecaptcha } = useGoogleReCaptcha() || {};

  const submit = useCallback(async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      let recaptcha_token = null;
      if (RECAPTCHA_SITE_KEY && typeof executeRecaptcha === "function") {
        try {
          recaptcha_token = await executeRecaptcha("register");
        } catch {
          recaptcha_token = null; // backend will reject if it requires one
        }
      }
      await register({ ...form, recaptcha_token });
      nav("/app/company");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
    finally { setBusy(false); }
  }, [executeRecaptcha, form, register, nav]);

  return (
    <AuthShell title="Create your account" subtitle="You'll complete company verification next.">
      <form onSubmit={submit} style={{ display: "grid", gap: 14 }} data-testid="register-form">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <button type="button" data-testid="role-buyer" onClick={() => set("role", "buyer")} className={`btn ${form.role === "buyer" ? "btn-primary" : "btn-ghost"}`} style={{ justifyContent: "center" }}>I'm a Buyer</button>
          <button type="button" data-testid="role-supplier" onClick={() => set("role", "supplier")} className={`btn ${form.role === "supplier" ? "btn-primary" : "btn-ghost"}`} style={{ justifyContent: "center" }}>I'm a Supplier</button>
        </div>
        <div><label className="label">Full name</label><input className="input" data-testid="reg-fullname" value={form.full_name} onChange={e => set("full_name", e.target.value)} required /></div>
        <div><label className="label">Company name</label><input className="input" data-testid="reg-company" value={form.company_name} onChange={e => set("company_name", e.target.value)} required /></div>
        <div><label className="label">Work email</label><input className="input" data-testid="reg-email" value={form.email} onChange={e => set("email", e.target.value)} type="email" required /></div>
        <div><label className="label">Phone</label><input className="input" data-testid="reg-phone" value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="+1-416…" /></div>
        <div><label className="label">Password</label><input className="input" data-testid="reg-password" value={form.password} onChange={e => set("password", e.target.value)} type="password" required minLength={6} /></div>
        {err && <div style={{ color: "var(--red)", fontSize: 13 }} data-testid="reg-error">{err}</div>}
        <button className="btn btn-primary" data-testid="reg-submit" disabled={busy} style={{ justifyContent: "center", padding: 12 }}>
          {busy ? "Creating…" : "Create account"}
        </button>
        {RECAPTCHA_SITE_KEY && (
          <div className="recaptcha-disclosure" data-testid="recaptcha-disclosure">
            This site is protected by reCAPTCHA. Google's{" "}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>{" "}and{" "}
            <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>{" "}apply.
          </div>
        )}
        <div style={{ fontSize: 13, color: "var(--ink-dim)", textAlign: "center" }}>
          Already on CanadaMRO? <Link to="/login" style={{ color: "var(--amber-400)" }}>Log in</Link>
        </div>
      </form>
    </AuthShell>
  );
}
